import { Settings as SettingsIcon, Moon, Globe, Bell, Terminal } from 'lucide-react';
import { useState } from 'react';
import s from './Pages.module.css';

interface AppSettings {
  theme: 'dark' | 'light' | 'system';
  network: 'testnet' | 'mainnet';
  notifications: boolean;
  editorFontSize: number;
  editorMinimap: boolean;
}

const defaults: AppSettings = {
  theme: 'dark',
  network: 'testnet',
  notifications: true,
  editorFontSize: 14,
  editorMinimap: true,
};

export function SettingsPage() {
  const [settings, setSettings] = useState<AppSettings>(defaults);

  const update = <K extends keyof AppSettings>(key: K, val: AppSettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: val }));
  };

  return (
    <div className={s.page}>
      <div className={s.pageHeader}>
        <h1 className={s.pageTitle}>Settings</h1>
        <p className={s.pageSubtitle}>Application preferences and configuration.</p>
      </div>

      {/* Appearance */}
      <div className={s.sectionHeader}>
        <h2 className={s.sectionTitle}><Moon size={16} /> Appearance</h2>
      </div>
      <div style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: 24, marginBottom: 32 }}>
        <div className={s.formGroup}>
          <label className={s.formLabel}>Theme</label>
          <select className={s.formSelect} value={settings.theme} onChange={e => update('theme', e.target.value as AppSettings['theme'])}>
            <option value="dark">Dark</option>
            <option value="light">Light</option>
            <option value="system">System Default</option>
          </select>
        </div>
      </div>

      {/* Network */}
      <div className={s.sectionHeader}>
        <h2 className={s.sectionTitle}><Globe size={16} /> Network</h2>
      </div>
      <div style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: 24, marginBottom: 32 }}>
        <div className={s.formGroup}>
          <label className={s.formLabel}>Default Network</label>
          <select className={s.formSelect} value={settings.network} onChange={e => update('network', e.target.value as AppSettings['network'])}>
            <option value="testnet">Testnet</option>
            <option value="mainnet">Mainnet</option>
          </select>
        </div>
      </div>

      {/* Notifications */}
      <div className={s.sectionHeader}>
        <h2 className={s.sectionTitle}><Bell size={16} /> Notifications</h2>
      </div>
      <div style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: 24, marginBottom: 32 }}>
        <div className={s.formGroup} style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <input type="checkbox" checked={settings.notifications} onChange={e => update('notifications', e.target.checked)} style={{ width: 18, height: 18, accentColor: 'var(--color-accent)' }} />
          <label className={s.formLabel} style={{ marginBottom: 0 }}>Enable in-app notifications</label>
        </div>
      </div>

      {/* Editor */}
      <div className={s.sectionHeader}>
        <h2 className={s.sectionTitle}><Terminal size={16} /> Editor</h2>
      </div>
      <div style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: 24, marginBottom: 32 }}>
        <div className={s.formGroup}>
          <label className={s.formLabel}>Font Size</label>
          <input
            type="number"
            className={s.formInput}
            value={settings.editorFontSize}
            min={10}
            max={24}
            onChange={e => update('editorFontSize', Number(e.target.value))}
          />
        </div>
        <div className={s.formGroup} style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <input type="checkbox" checked={settings.editorMinimap} onChange={e => update('editorMinimap', e.target.checked)} style={{ width: 18, height: 18, accentColor: 'var(--color-accent)' }} />
          <label className={s.formLabel} style={{ marginBottom: 0 }}>Show minimap</label>
        </div>
      </div>

      {/* About */}
      <div className={s.sectionHeader}>
        <h2 className={s.sectionTitle}><SettingsIcon size={16} /> About</h2>
      </div>
      <div style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: 24 }}>
        <table className={s.table}>
          <tbody>
            <tr><td style={{ fontWeight: 500 }}>Version</td><td>1.0.0</td></tr>
            <tr><td style={{ fontWeight: 500 }}>Crypto</td><td>ML-DSA-65 / CRYSTALS-Dilithium3 (post-quantum)</td></tr>
            <tr><td style={{ fontWeight: 500 }}>VM</td><td>Mist runtime (deployable contract path)</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
